import React from 'react';
import PropTypes from 'prop-types';

import { mainHeader, subHeader, subjectArea } from './Info.less';

import { mapMSNQTotalToCognitiveSymptoms, mapMSNQScoresToCognitiveChallenges } from 'neuro-calculation-commons';

const cognitiveSymptomsLocalizationsSV = {
  mild: 'På basis av förfrågan upplever du högst tidvisa/lindriga kognitiva symtom',
  moderate: 'På basis av förfrågan upplever du minst tidvisa/lindriga kognitiva symtom',
  severe: 'På basis av förfrågan upplever du störande kognitiva symtom i vardagen',
  verySevere: 'På basis av förfrågan upplever du betydande kognitiva symtom'
};

const cognitiveChallengesLocalizationsSV = {
  concentration: 'i koncentrationen',
  memoryLearning: 'i att lära dig något nytt och/eller i att minnas',
  problemSolving: 'i att lösa ett problem och/eller prestera språkligt och/eller att reglera beteendet'
};

const InfoSV = ({ totalScore, statusMood, statusFatigue, document }) => (
  <React.Fragment>
    <div className={mainHeader}>
      Sammandrag av hur du upplever din kognitiva funktionsförmåga (på basis av den ifyllda MSNQ- blanketten)
    </div>

    <div className={subjectArea}>
      <div className={subHeader}>Totalantalet poäng</div>
      <ul>
        <li>
          Du fick av förfrågan <span style={{ fontWeight: 600 }}>{totalScore}</span> poäng (minsta möjliga poängantal 0,
          största möjliga 60; ett större poängtal tyder på avsevärda kognitiva symtom).
        </li>
      </ul>
    </div>

    <div className={subjectArea}>
      <div className={subHeader}>Dina upplevda kognitiva symtom</div>
      <ul>
        <li>{cognitiveSymptomsLocalizationsSV[mapMSNQTotalToCognitiveSymptoms(totalScore)]}</li>
      </ul>
    </div>

    {mapMSNQScoresToCognitiveChallenges(document.content).length > 0 && (
      <div>
        <ul>
          <li>å basis av förfrågan kan du åtminstone tidvis uppleva utmaningar:</li>
          <ul>
            {mapMSNQScoresToCognitiveChallenges(document.content).map(challenge => (
              <li>{cognitiveChallengesLocalizationsSV[challenge]}</li>
            ))}
          </ul>
        </ul>
      </div>
    )}

    {(statusFatigue > 0 || statusMood > 0) && (
      <div className={subjectArea}>
        <span>
          Det är bra att lägga märke till att de kognitiva symtom du upplever åtminstone delvis kan förklaras av
          {statusFatigue > 0 && ' den utmattning du upplever'} {statusFatigue > 0 && statusMood > 0 && ' och'}{' '}
          {statusMood > 0 && ' nedstämd sinnesstämning'}. Du tar väl upp en eventuell utmattning och nedstämdhet vid
          samtal med MS-skötaren eller din läkare.
        </span>
      </div>
    )}

    <div className={subjectArea}>
      <div className={subHeader}>Information om kognitiva symtom</div>
      De mest typiska kognitiva symtomen som har ett samband med MS, är att det är arbetsamt att lära sig något nytt,
      det går långsammare att behandla information, att koncentrationen utsätts för störningar och att
      tankeverksamheterna lättare än förut belastas. Man måste reservera mera tid än förut för att uträtta ärenden och i
      förväg ta i betraktande att verksamheten inte blir genomförd på det sätt som planerats i förväg. Ibland kan det
      också förekomma svårigheter med att finna ord – i synnerhet när man är trött kan till och med bekanta ord helt
      överraskande försvinna. I vissa fall kan det även vara svårt att gestalta omgivningen, t.ex. kan det bli svårare
      att röra sig på bekanta ställen. Speciellt vid en belastning kan det bli svårare att reglera den egna verksamheten
      och inte heller löper tankeverksamheten då lika smidigt som när man är pigg. Det är sällan som alla symtomen ovan
      samtidigt är förenade med MS.
      <br />
      <br />
      Vanligtvis kommer man väl till rätta med de lindriga symtomen genom att fästa uppmärksamhet på sina egna
      handlingssätt och genom att utnyttja olika slag av stödfunktioner, som de flesta tar i bruk utan att lägga märke
      till dem. Följande tips kan vara till hjälp för dig:
    </div>

    <div className={subjectArea}>
      <div className={subHeader}>Hjälp i vardagen</div>
      Koncentrationen
      <br />
      <ul>
        <li>om din koncentration lätt blir störd, sträva efter att gallra bort störande faktorer omkring dig</li>
        <ul>
          <li>sträva efter att utföra uppgifterna i en miljö så fri från störande faktorer som möjligt</li>
          <li>använd hörlurar vid behov, koppla från radio / TV / telefon</li>
          <li>undvik brådska och att göra något i sista minuten</li>
        </ul>
        <li>
          om det är svårt att koncentrera sig på flera saker samtidigt, försök göra en sak till slut innan du går över
          till följande
        </li>
        <ul>
          <li>undvik situationer, där du har många saker på hälft</li>
          <li>gör upp en lista över uppgifter för dig själv</li>
          <li>reservera tillräckligt med tid för dig själv</li>
        </ul>
        <li>
          om det medför svårigheter med att upprätthålla koncentrationen en längre tid och/eller du lätt blir trött, ta
          en paus i att göra något
        </li>
        <ul>
          <li>
            ta korta pauser med lämpliga mellanrum: lyssna en stund på musik, slut ögonen, gör lite kort pausgymnastik
            o.d.
          </li>
          <li>
            sträva efter att utföra de viktigaste och mest belastande uppgifterna, när du känner att du är piggast
          </li>
          <li>undvik långa arbetspass i en följd, möten o.d. och föreslå att man tar en paus</li>
        </ul>
      </ul>
      <br />
      Att lära sig nytt, att komma ihåg
      <ul>
        <li>om det är mera arbetsdrygt att lära sig något nytt, reservera mera tid för det än tidigare</li>
        <ul>
          <li>koncentrera dig på det väsentliga, var systematisk, gör inlärningsmiljön lugn</li>
          <li>ge inlärningen tillräckligt med tid, pausa</li>
          <li>dela upp det som ska läras in, i mindre helheter</li>
          <li>försök upprepa det du lärt dig, genast när du lärt dig saken</li>
          <li>
            identifiera, hur du bäst lär dig något och utnyttja sådana stödfunktioner för inlärningen som passar dig:
            bilder, ordlistor, inlärningsvideor osv.
          </li>
        </ul>
        <li>om du lättare glömmer det tidigare, använd olika sätt för att stödja minnet</li>
        <ul>
          <li>
            endast fantasin sätter en gräns – anteckningshäften, kalendrar, minneslappar, alarm osv. är synnerligen
            användbara, om du bara använder dem systematiskt
          </li>
          <li>
            kom överens med dina nära om en systematisk användning av stödfunktioner för minnet, bl.a. av att använda en
            kalender
          </li>
          <li>
            sträva också efter att i studierna, på arbetsplatsen och fritidssysselsättningarna komma överens om att
            använda stödsätt för att komma ihåg något
          </li>
        </ul>
      </ul>
      <br />
      Kognitiv flexibilitet, språklig prestation, reglering av beteendet osv.
      <ul>
        <li>
          om nya situationer, som avviker från det vanliga, är utmanande för dig, sträva efter att ta dig tid för att
          lösa saken
        </li>
        <ul>
          <li>stanna upp en stund och överväg alternativ för lösningen – en liten paus gör att man tänker klarare</li>
          <li>spjälka upp den sak som ska åtgärdas i mindre helheter och gör en sak i sänder</li>
        </ul>
        <li>om det bereder svårigheter att finna orden, försök dämpa takten i talet och ta dig själv tid</li>
        <ul>
          <li>
            du kan tala om för dina närstående, om du önskar mera tid för att berätta ditt budskap eller för att söka
            orden
          </li>
        </ul>
        <li>
          om du upplever att du beter dig på ett avvikande sätt för dig själv, ta saken på tal med dina närstående
        </li>
        <ul>
          <li>
            fundera tillsammans över vad förändringen kan bero på – osäkerhet, utmattning och nedstämdhet som vållas av
            sjukdomen, kan påverka handlingssätten och beteendet
          </li>
          <li>sträva efter att identifiera och undvika situationer eller faktorer som utlöser känsloreaktioner</li>
        </ul>
        <li>störningsfaktorerna, belastning och utmattning kan ge kort stubin</li>
        <ul>
          <li>försök gallra bort störningsfaktorer</li>
          <li>ge akt på hur du orkar, pausa och vila</li>
          <li>
            sträva efter att med dina närstående komma överens om handlingssätt som stöder att du orkar (se
            utmattningsavsnittet i MyMS)
          </li>
        </ul>
      </ul>
    </div>
    <div className={subjectArea}>
      <div className={subHeader}>Hur gå vidare härifrån</div>
      Det är viktigt att du håller dig aktiv trots dina kognitiva symtom. När man har något meningsfullt att göra och
      hjärnan arbetar stöder det att den kognitiva funktionsförmågan bevaras god. Om dina kognitiva symtom medför
      utmaningar med att klara dig i vardagen, till exempel gå vidare i studierna eller klara dig i arbetet, ta upp
      symtomen vid samtal med MS-skötaren och/eller din läkare. Tillsammans kan ni bedöma de fortsatta åtgärderna, till
      exempel behovet av en neuropsykologisk undersökning. Det är möjligt att noggrannare utreda den kognitiva
      funktionsförmågan och du kan dra nytta av handledning och rehabilitering. Mera information om
      rehabiliteringsmöjligheterna finner du exempelvis på Fpa:s webbplats{' '}
      <a href="https://www.kela.fi" target="blank">
        www.kela.fi
      </a>{' '}
      (MS-rehabiliteringskurser, krävande, individuell, multiprofessionell rehabilitering på den neurologiska linjen
      enligt prövning, neuropsykologisk rehabilitering) och på Neuroförbundets webbplats{' '}
      <a href="https://www.kuntoutukseen.fi" target="blank">
        www.kuntoutukseen.fi
      </a>
      . Det är också bra att berätta om symtomen för dina närstående. Du kan utnyttja det sammandrag av den här
      förfrågan som du fått samt den handbok Kognitio -opas som finns på Neuroförbundets webbplats (
      <a href="https://neuroliitto.fi/tuote/kognitio-opas/" target="blank">
        Kognitio-opas
      </a>
      {', '}
      <a href="https://neuroliitto.fi/tuote/kognitiiviset-oireet-tyoelamassa/" target="blank">
        Kognitiiviset oireet työelämässä
      </a>
      ).
    </div>
  </React.Fragment>
);

InfoSV.propTypes = {
  totalScore: PropTypes.number,
  statusMood: PropTypes.number,
  statusFatigue: PropTypes.number,
  document: PropTypes.object
};

export default InfoSV;
